import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { TrendUp, Trophy } from "@phosphor-icons/react";
import { getApiErrorMessage } from "@/lib/apiError";
import { PageHeader } from "@/components/PageHeader";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatCurrency = (value) => {
  return new Intl.NumberFormat('pt-PT', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2
  }).format(value || 0);
};

const formatPercent = (fraction) => {
  return `${(fraction * 100).toFixed(1).replace('.0', '').replace('.', ',')}%`;
};

export const RappelClientCard = ({ client }) => {
  if (!client?.tier_breakdown?.length) return null;
  const hasSalesData = client.matched_sales_names && client.matched_sales_names.length > 0;

  return (
    <div className="gresart-card" data-testid={`rappel-card-${client.client_number}`}>
      <div className="flex items-start justify-between gap-2 mb-3">
        <div>
          <p className="text-xs text-stone-400">Nº {client.client_number}</p>
          <h3 className="font-bold text-stone-900 leading-tight">{client.client_name}</h3>
        </div>
        {client.max_tier_reached && (
          <span className="flex items-center gap-1 text-xs font-semibold text-amber-600 bg-amber-50 px-2 py-1 rounded-full flex-shrink-0">
            <Trophy size={14} weight="fill" /> Máximo
          </span>
        )}
      </div>

      {!hasSalesData && (
        <p className="text-xs text-stone-500 bg-stone-100 rounded-lg px-2.5 py-1.5 mb-3" data-testid={`rappel-no-sales-${client.client_number}`}>
          Sem dados de vendas correspondentes ainda - progresso não pode ser calculado.
        </p>
      )}

      <p className="text-sm font-semibold text-[#009392] mb-3" data-testid={`rappel-current-sales-${client.client_number}`}>
        Faturação atual: {formatCurrency(client.current_sales)}
      </p>

      <div className="space-y-3">
        {client.tier_breakdown.map((tier) => (
          <div key={tier.tier_number} data-testid={`rappel-tier-${client.client_number}-${tier.tier_number}`}>
            <div className="flex items-center justify-between text-xs mb-1">
              <span className="font-semibold text-stone-700">Escalão {tier.tier_number}</span>
              <span className="text-stone-500">
                Objetivo: <span className="font-medium text-stone-700">{formatCurrency(tier.objetivo)}</span>
                {' · '}
                <span className="font-medium text-[#009392]">{formatPercent(tier.percentual)}</span>
              </span>
            </div>
            <div className="relative h-2.5 bg-stone-100 rounded-full overflow-hidden">
              <div
                className={`absolute inset-y-0 left-0 rounded-full transition-all duration-700 ${tier.reached ? 'bg-emerald-500' : 'bg-gradient-to-r from-[#009392] to-[#00b3b0]'}`}
                style={{ width: `${tier.progress_percentage}%` }}
                data-testid={`rappel-progress-bar-${client.client_number}-${tier.tier_number}`}
              />
            </div>
            {tier.reached ? (
              <p className="text-xs text-emerald-600 font-bold mt-1 flex items-center gap-1" data-testid={`rappel-alert-${client.client_number}-${tier.tier_number}`}>
                <Trophy size={12} weight="fill" /> Objetivo Alcançado!
              </p>
            ) : (
              <p className="text-xs text-amber-700 mt-1" data-testid={`rappel-alert-${client.client_number}-${tier.tier_number}`}>
                Faturado: <span className="font-bold">{tier.progress_percentage}%</span>
                {' | '}
                Faltam: <span className="font-bold">{formatCurrency(tier.amount_remaining)}</span> ({(Math.round((100 - tier.progress_percentage) * 10) / 10)}%)
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default function RappelPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchRappel = async () => {
      try {
        const response = await axios.get(`${API}/rappel`);
        setData(response.data);
      } catch (error) {
        toast.error("Erro ao carregar Rappel: " + getApiErrorMessage(error));
      } finally {
        setLoading(false);
      }
    };
    fetchRappel();
  }, []);

  return (
    <div className="min-h-screen bg-stone-50 pb-10" data-testid="rappel-page">
      <div className="bg-white border-b border-stone-200 px-4 py-4 sticky top-0 z-10">
        <PageHeader
          icon={TrendUp}
          title={`RAPPEL ${data?.year || ''}`}
          subtitle="Objetivos de faturação e bonificação por distribuidor"
        />
      </div>

      <div className="max-w-3xl mx-auto px-4 py-6 space-y-4">
        {loading ? (
          <p className="text-center text-stone-400 py-10">A carregar...</p>
        ) : !data?.clients?.length ? (
          <p className="text-center text-stone-400 py-10">Nenhum objetivo de Rappel configurado.</p>
        ) : (
          <>
            {data.sales_last_updated && (
              <p className="text-xs text-stone-400 text-center" data-testid="rappel-sales-last-updated">
                Vendas atualizadas em {new Date(data.sales_last_updated).toLocaleDateString('pt-PT')}
              </p>
            )}
            {data.clients.map((client) => (
              <RappelClientCard key={client.client_number} client={client} />
            ))}
          </>
        )}
      </div>
    </div>
  );
}
